import type { SearchClient, UiState } from "instantsearch.js";
import { history } from "instantsearch.js/es/lib/routers";

import type { SortOption } from "@/composites/product-listing";
import type { ListingSearchParams } from "@/domain/product-listing";
import {
	LISTING_PAGE_PARAM,
	MAX_LISTING_PAGES,
	parseListingPages
} from "@/domain/product-listing/listing-depth";
import {
	isListingSort,
	LISTING_SORTS,
	listingFilterKind,
	listingFilterParam,
	type ListingFacetAttribute,
	type ListingSort
} from "@/domain/product-listing/listing-facets";

export const LISTING_INDEX = "products";
export const LISTING_FACET_LIMIT = 100;

const SORT_PARAM = "sort";
const VALUE_SEPARATOR = ",";
const RANGE_SEPARATOR = ":";
const TOGGLE_ON = "true";

const SORT_LABELS: Record<ListingSort, string> = {
	price_asc: "Price: Low to High",
	price_desc: "Price: High to Low",
	rating_desc: "Top Rated",
	newest: "Newest"
};

export const LISTING_SORT_OPTIONS: SortOption[] = [
	{ value: LISTING_INDEX, label: "Relevance" },
	...LISTING_SORTS.map((sort) => ({
		value: sortIndex(sort),
		label: SORT_LABELS[sort]
	}))
];

export function sortIndex(sort: ListingSort): string {
	return `${LISTING_INDEX}_${sort}`;
}

function sortOf(indexName: string | undefined): ListingSort | undefined {
	const sort = indexName?.slice(LISTING_INDEX.length + 1);
	return isListingSort(sort) ? sort : undefined;
}

type RouteState = Record<string, string[]>;

export function toQueryString(route: RouteState): string {
	const params = Object.keys(route)
		.filter((param) => route[param].length > 0)
		.sort()
		.map((param) => {
			const values = [...route[param]].sort().map(encodeURIComponent);
			return `${encodeURIComponent(param)}=${values.join(VALUE_SEPARATOR)}`;
		});
	return params.length > 0 ? `?${params.join("&")}` : "";
}

export function parseQueryString(
	search: string,
	params: readonly string[]
): RouteState {
	const query = new URLSearchParams(search);
	const route: RouteState = {};
	for (const param of params) {
		const values = (query.get(param) ?? "")
			.split(VALUE_SEPARATOR)
			.filter(Boolean);
		if (values.length > 0) route[param] = values;
	}
	return route;
}

type IndexUiState = UiState[string];

export function toRoute(
	state: IndexUiState | undefined,
	attributes: readonly ListingFacetAttribute[]
): RouteState {
	const route: RouteState = {};
	for (const attribute of attributes) {
		const param = listingFilterParam(attribute);
		switch (listingFilterKind(attribute)) {
			case "list": {
				const values = state?.refinementList?.[attribute] ?? [];
				if (values.length > 0) route[param] = values;
				break;
			}
			case "toggle":
				if (state?.toggle?.[attribute]) route[param] = [TOGGLE_ON];
				break;
			case "range": {
				const range = state?.range?.[attribute];
				if (range && range !== RANGE_SEPARATOR) route[param] = [range];
				break;
			}
		}
	}
	const sort = sortOf(state?.sortBy);
	if (sort) route[SORT_PARAM] = [sort];
	const pages = Math.min(state?.page ?? 1, MAX_LISTING_PAGES);
	if (pages > 1) route[LISTING_PAGE_PARAM] = [String(pages)];
	return route;
}

export function toIndexUiState(
	route: RouteState,
	attributes: readonly ListingFacetAttribute[]
): IndexUiState {
	const state: Required<
		Pick<IndexUiState, "refinementList" | "toggle" | "range">
	> &
		IndexUiState = { refinementList: {}, toggle: {}, range: {} };
	for (const attribute of attributes) {
		const values = route[listingFilterParam(attribute)];
		if (!values?.length) continue;
		switch (listingFilterKind(attribute)) {
			case "list":
				state.refinementList[attribute] = values;
				break;
			case "toggle":
				if (values[0] === TOGGLE_ON) state.toggle[attribute] = true;
				break;
			case "range":
				if (values[0].includes(RANGE_SEPARATOR)) {
					state.range[attribute] = values[0];
				}
				break;
		}
	}
	const sort = route[SORT_PARAM]?.[0];
	if (isListingSort(sort)) state.sortBy = sortIndex(sort);
	const pages = parseListingPages(route[LISTING_PAGE_PARAM]?.[0]);
	if (pages && pages > 1) state.page = pages;
	return state;
}

export function createListingRouting(
	attributes: readonly ListingFacetAttribute[]
) {
	const params = [
		...attributes.map((attribute) => listingFilterParam(attribute)),
		SORT_PARAM,
		LISTING_PAGE_PARAM
	];
	return {
		router: history<RouteState>({
			cleanUrlOnDispose: false,
			getLocation: () =>
				typeof window === "undefined"
					? (new URL("http://localhost/") as unknown as Location)
					: window.location,
			createURL: ({ routeState, location }) =>
				`${location.origin}${location.pathname}${toQueryString(routeState)}${location.hash}`,
			parseURL: ({ location }) =>
				parseQueryString(location.search, params)
		}),
		stateMapping: {
			stateToRoute: (uiState: UiState): RouteState =>
				toRoute(uiState[LISTING_INDEX], attributes),
			routeToState: (route: RouteState): UiState => ({
				[LISTING_INDEX]: toIndexUiState(route, attributes)
			})
		}
	};
}

export interface ListingSearchResponse {
	results: unknown[];
}

export function createListingSearchClient(
	search: (
		searches: ListingSearchParams[]
	) => Promise<ListingSearchResponse | null>
): SearchClient {
	return {
		search: async (
			requests: { indexName?: string; params?: ListingSearchParams }[]
		) => {
			const response = await search(
				requests.map((request) => ({
					...request.params,
					sort: sortOf(request.indexName)
				}))
			);
			if (!response) throw new Error("Listing search failed");
			return response;
		}
	} as unknown as SearchClient;
}
